<template>
    <div>
        <div>
        	<p class="mb-2 font-weight-bold">{{$t('date-time')}}</p>
            <DateInput
                :label="$t('from')"
                v-model="from"
                :min="timetable.datefrom"
                :max="to < timetable.dateto ? to : timetable.dateTo"
                class="mb-2"
            />
            <DateInput
                :label="$t('to')"
                :disabled="!from"
                v-model="to"
                :min="from > timetable.datefrom ? from : timetable.datefrom"
                :max="timetable.dateto"
                class="mb-2"
            />
            <button
                type="button"
                class="btn btn-outline-primary mb-3 mr-1"
                @click="gotoDates"
            >
                {{ $t('go') }}<b-icon icon="arrow-right" class="ml-2" />
            </button>
            <button
                v-show="from"
                type="button"
                class="btn btn-outline-danger mb-3"
                @click="$router.replace({ 'query': { ...$route.query.params, from: undefined, to: undefined }})"
            >
                {{ $t('clear') }}
            </button>
        </div>
        <div class="mb-2">
        	<p class="mb-2 font-weight-bold">{{$t('filter-by-group-or-year')}}</p>
            <YesNoToggle
                :label="$t('filter-by-group-or-year')"
                :srOnly="true"
                v-model="filterByGroups"
                :yesLabel="$t('groups')"
                :noLabel="$t('years')"
                :noFirst="true"
                class="mb-2 mr-auto"
                @input="() => {$emit('updateGroups', []); $emit('updateYears', []);}"
            />
			<YearMultiSelect
				v-show="!filterByGroups"
				:value="innerYears"
				@input="val => $emit('updateYears', val)"
				:mini="true"
			/>
			<GroupMultiSelect
				v-show="filterByGroups"
				:value="innerGroups"
				@input="val => $emit('updateGroups', val)"
				:groups="allGroups"
				:mini="true"
			/>
		</div>
		<div class="mb-2">
        	<p class="mb-2 font-weight-bold">{{$t('students')}}</p>
			<UserSelector
                v-if="timetable.students"
				:value="innerUsers"
				@input="val => $emit('updateUsers', val)"
				:users="timetable.students.map(st => { return { ...st, id: st.user_id ? st.user_id : st.id }})"
				:label="`${innerUsers.length} ${$t('students-selected')}`"
				:showSelectedOnEdit="true"
			>
				<template v-slot:label>
					<div class="d-flex flex-row align-items-center justify-content-between">
						<div class="list-group-item text-sm font-weight-bold">{{ `${innerUsers.length} ${$t('students-selected')}` }}</div>
						<button v-if="innerUsers.length" type="button" class="btn btn-sm btn-outline-danger" @click="$emit('updateUsers', [])">
							{{ $t('clear') }}
						</button>
					</div>
				</template>
			</UserSelector>
		</div>
		<div>
			<RoomSelector
				:rooms="allRooms"
				:value="innerRooms"
				@input="val => $emit('updateRooms', val)"
				:label="$t('rooms')"
				class="list-group"
				selectorStyle="tag"
			/>
			<div v-show="innerRooms.length" class="">
				<DateInput
					:label="$t('get-students-here-on-the-colon')"
					v-model="roomSearchDate"
					:min="timetable.datefrom"
					:max="timetable.dateto"
				/>
				<button
					type="button"
					class="btn btn-outline-primary mb-3"
					@click="$emit('searchRoomsOnDate', roomSearchDate)"
				>
					{{ $t('go') }}<b-icon icon="arrow-right" class="ml-2" />
				</button>
			</div>
		</div>
    </div>
</template>

<script>
import SessionManager from "src/modules/session/sessionManager"

import GroupMultiSelect from "src/components/ProgrammeYearGroups/GroupMultiSelect.vue";
import YearMultiSelect from "src/components/ProgrammeYearGroups/YearMultiSelect.vue";
import YesNoToggle from "src/components/Inputs/YesNoToggle";
import DateInput from "src/components/Inputs/DateInput";
import RoomSelector from "src/components/Rooms/RoomSelector";
import UserSelector from "src/components/Users/UserSelector";

export default {
    components: {
		GroupMultiSelect,
		YearMultiSelect,
		DateInput,
		YesNoToggle,
		RoomSelector,
		UserSelector,
	},
    emits: ['updateGroups', 'updateYears', 'updateRooms', 'updateUsers', 'slimRows', 'searchRoomsOnDate', 'updateFrom', 'updateTo'],
    props: {
        timetable: {
            type: Object,
            required: true,
        },
        allGroups: {
            type: Array,
            default: () => [],
        },
        selectedGroups: {
            type: Array,
            default: () => [],
        },
        selectedYears: {
            type: Array,
            default: () => [],
        },
        allRooms: {
            type: Array,
            default: () => [],
        },
        selectedRooms: {
            type: Array,
            default: () => [],
        },
        selectedUsers: {
            type: Array,
            default: () => [],
        },
        slimRows: Boolean,
        isPlacementTT: { // quick fix, need to do this properly later
            type: Boolean,
            default: () => false
        } 
    },
    mounted() {
        // This ensures that any filter actions (such as navigating between groups and years)
        // trigger a save of state in SessionManager.
        this.emit = this.$emit.bind(this)
        this.$emit = (key, value) => {
            this.emit(key, value)
            this.$nextTick(this.saveState)
        }

        this.readState()
    },
    updated() {
        this.saveState()
    },
    data() {
        return {
            emit: null,
            sessionManager: new SessionManager(this.$route),
            innerGroups: this.selectedGroups,
            innerYears: this.selectedYears,
            innerRooms: this.selectedRooms,
            innerUsers: this.selectedUsers,

            filterByGroups: false,
            roomSearchDate: '',
            from: this.$route.query.from,
            to: this.$route.query.to,
            
        }
    },
    methods: {
        saveState() {
            this.sessionManager.set('filter-progress', {
                innerGroups: this.innerGroups,
                innerYears: this.innerYears,
                innerUsers: this.innerUsers,
                slimRows: this.slimRows,
            })
        },
        readState() {
            const state = this.sessionManager.get('filter-progress')

            if (!state) {
                if (this.$route.query.programme) {
                    const programme = this.$config.getValue('programmes')[this.$route.query.programme]

                    if (this.$route.query.year) {
                        const pyg = { 
                            programme_id: programme.Id,
                            year: this.$route.query.year 
                        }
                        if (this.$route.query.group) {
                            pyg.group_id = this.$route.query.group
                            pyg.id = `${pyg.programme_id}_${pyg.year}_${pyg.group_id}`
                            this.innerGroups.push(pyg)
                            this.filterByGroups = true

                        }
                        else this.innerYears.push(pyg)
                    }
                }
                return
            }

            this.innerGroups = state.innerGroups; this.emit('updateGroups', this.innerGroups);
            this.innerYears = state.innerYears; this.emit('updateYears', this.innerYears);
            this.innerUsers = state.innerUsers; this.emit('updateUsers', this.innerUsers);
            this.emit('slimRows', state.slimRows);
        },
        gotoDates() {
            if (this.$route.query.params && (this.$route.query.params.from || this.$route.query.params.to)) {
                this.$router.replace({ 'query': { ...this.$route.query.params, from: undefined, to: undefined }})    
            }
            
            this.$router.push(`/timetables/${this.isPlacementTT ? 'placements' : 'edit/'+this.timetable.id }?${this.from ? 'from='+this.from : ''}${this.to ? '&to='+this.to : ''}`)
        },
        clearDataes() {
            this.$router.replace({ 'query': { ...this.$route.query.params, from: undefined, to: undefined }})
        }
    },
    watch: {
        selectedGroups(newGroups) {
            this.innerGroups = newGroups
            if (newGroups.length) this.filterByGroups = true
        },
        selectedYears(newYears) {
            this.innerYears = newYears
            if (newYears.length) this.filterByGroups = false
        },
        selectedRooms(newRooms) {
            this.innerRooms = newRooms
        },
        selectedUsers(newUsers) {
            this.innerUsers = newUsers
        },
    }
}
</script>
